import { __assign } from '../node_modules/tslib/tslib.es6.mjs.js';
import React, { useEffect, useRef, useState, useMemo } from 'react';
import RsDateRangePicker from 'rsuite/DateRangePicker';
import { differenceInCalendarDays, format, addDays, startOfMonth, addMonths, endOfMonth, startOfYear, addYears, endOfYear } from 'date-fns';
import 'rsuite/dist/rsuite-no-reset.min.css';
import { FormControl, Button, FormLabel, FormHelperText, FormErrorMessage } from '@chakra-ui/react';
import Typography from './Typography.js';
import DateRangePickerStyles from './DateRangePicker.css.js';
import { getCurrentLanguage, getDirection, t } from '../utils/i18n.js';
import Calendar from '../icons/Calendar.js';
import Question from '../icons/Question.js';
import CloseFilled from '../icons/CloseFilled.js';

var predefinedRanges = function (allTimeStartDate) {
    var validAllTimeStartDate = allTimeStartDate instanceof Date && !isNaN(allTimeStartDate.getTime())
        ? allTimeStartDate
        : new Date("2023-07-01");
    return [
        {
            label: t("Today"),
            value: [new Date(), new Date()],
            placement: "left",
        },
        {
            label: t("Yesterday"),
            value: [addDays(new Date(), -1), addDays(new Date(), -1)],
            placement: "left",
        },
        {
            label: t("Last 7 days"),
            value: [addDays(new Date(), -7), new Date()],
            placement: "left",
        },
        {
            label: t("Last 30 days"),
            value: [addDays(new Date(), -30), new Date()],
            placement: "left",
        },
        {
            label: t("Last 90 days"),
            value: [addDays(new Date(), -90), new Date()],
            placement: "left",
        },
        {
            label: t("Last 365 days"),
            value: [addDays(new Date(), -365), new Date()],
            placement: "left",
        },
        {
            label: t("Last month"),
            value: [
                startOfMonth(addMonths(new Date(), -1)),
                endOfMonth(addMonths(new Date(), -1)),
            ],
            placement: "left",
        },
        {
            label: t("Last 12 months"),
            value: [
                startOfMonth(addMonths(new Date(), -12)),
                endOfMonth(addMonths(new Date(), -1)),
            ],
            placement: "left",
        },
        {
            label: t("Last year"),
            value: [
                startOfYear(addYears(new Date(), -1)),
                endOfYear(addYears(new Date(), -1)),
            ],
            placement: "left",
        },
        {
            label: t("All time"),
            value: [validAllTimeStartDate, new Date()],
            placement: "left",
        },
    ];
};
var DateRangePicker = function (props) {
    var onDateValueChange = props.onDateValueChange, size = props.size, isRequired = props.isRequired, isInvalid = props.isInvalid, id = props.id, error = props.error, helpText = props.helpText, _a = props.name, name = _a === void 0 ? "DD/MM/YYYY - DD/MM/YYYY" : _a, shouldDisableDate = props.shouldDisableDate, showLabel = props.showLabel, defaultFromDate = props.defaultFromDate, defaultToDate = props.defaultToDate, shortcut = props.shortcut, setShortCut = props.setShortCut, allTimeStartDate = props.allTimeStartDate, menuStyle = props.menuStyle, _b = props.showPredefinedRanges, showPredefinedRanges = _b === void 0 ? true : _b, _c = props.isMobile, isMobile = _c === void 0 ? false : _c;
    var currentLanguage = getCurrentLanguage();
    var direction = getDirection(currentLanguage);
    useEffect(function () {
        var applyDirectionToPopup = function () {
            var popupElement = document.querySelector(".rs-picker-popup-daterange");
            if (popupElement) {
                popupElement.style.setProperty("direction", direction);
            }
        };
        applyDirectionToPopup();
        var observer = new MutationObserver(function (mutations) {
            mutations.forEach(function (mutation) {
                if (mutation.type === "childList") {
                    mutation.addedNodes.forEach(function (node) {
                        if (node.nodeType === Node.ELEMENT_NODE) {
                            var element = node;
                            if (element.classList.contains("rs-picker-popup-daterange")) {
                                element.style.setProperty("direction", direction);
                            }
                        }
                    });
                }
            });
        });
        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });
        return function () {
            observer.disconnect();
        };
    }, [direction, id]);
    var rangePickerRef = useRef(null);
    var _d = useState(), startDate = _d[0], setStartDate = _d[1];
    var _e = useState(), endDate = _e[0], setEndDate = _e[1];
    var _f = useState(false), focus = _f[0], setFocus = _f[1];
    var renderBoldTitle = function (date) {
        var formattedTitle = date.toLocaleString("en-US", {
            month: "long",
            year: "numeric",
        });
        return (React.createElement(Typography, { variant: "h3", fontWeight: 700, color: "#121417" }, formattedTitle));
    };
    var handleOpen = function () {
        var _a;
        if ((_a = rangePickerRef.current) === null || _a === void 0 ? void 0 : _a.open)
            rangePickerRef.current.open();
    };
    var triggerButtonLabel = useMemo(function () {
        if (shortcut) {
            if (isMobile && shortcut === t("Last 30 days")) {
                return t("30d");
            }
            return shortcut;
        }
        if (isMobile && startDate && endDate) {
            var inclusiveDays = differenceInCalendarDays(endDate, startDate) + 1;
            return "".concat(inclusiveDays, "d");
        }
        var from = startDate ? format(startDate, "dd/MM/yyyy") : "DD/MM/YYYY";
        var to = endDate ? format(endDate, "dd/MM/yyyy") : "DD/MM/YYYY";
        return "".concat(from, " - ").concat(to);
    }, [shortcut, isMobile, startDate, endDate]);
    return (React.createElement(FormControl, { id: id, variant: "floating", isRequired: isRequired !== null && isRequired !== void 0 ? isRequired : false, style: __assign({ position: "relative" }, (isMobile ? { width: "fit-content", maxWidth: "100%" } : {})), isInvalid: isInvalid !== null && isInvalid !== void 0 ? isInvalid : false },
        React.createElement(Button, { onClick: handleOpen, variant: "outline", width: isMobile ? "fit-content" : "100%", height: size === "sm" ? "36px" : "48px", borderRadius: "6px", leftIcon: React.createElement(Calendar, null), borderColor: isInvalid ? "red.500" : undefined, _active: { backgroundColor: "white" }, dir: direction }, triggerButtonLabel),
        React.createElement(RsDateRangePicker, { ref: rangePickerRef, ranges: showPredefinedRanges ? predefinedRanges(allTimeStartDate) : [], block: true, id: "date-range-picker-".concat(id), dir: direction, placement: "bottomStart", preventOverflow: true, onFocus: function () {
                setFocus(true);
            }, onBlur: function () {
                setFocus(false);
            }, defaultValue: defaultFromDate && defaultToDate
                ? [defaultFromDate, defaultToDate]
                : null, shouldDisableDate: shouldDisableDate, renderTitle: renderBoldTitle, renderValue: function (value) {
                var start = format(value[0], "dd/MM/yyyy");
                var end = format(value[1], "dd/MM/yyyy");
                if (shortcut) {
                    if (isMobile && shortcut === t("Last 30 days")) {
                        return t("30d");
                    }
                    return shortcut;
                }
                return "".concat(start, " ~ ").concat(end);
            }, format: "dd/MM/yyyy", placeholder: name, onChange: function (value) {
                if (value) {
                    setShortCut(null);
                    setStartDate(value === null || value === void 0 ? void 0 : value[0]);
                    setEndDate(value === null || value === void 0 ? void 0 : value[1]);
                    onDateValueChange(value[0], value[1]);
                }
            }, size: size, className: "".concat(shortcut === null || shortcut === void 0 ? void 0 : shortcut.replace(/\s+/g, "")), menuClassName: "".concat(shortcut === null || shortcut === void 0 ? void 0 : shortcut.replace(/\s+/g, "")), menuStyle: menuStyle, showOneCalendar: true, onShortcutClick: function (shortcut, event) {
                if (shortcut.value) {
                    setShortCut(shortcut.label.toString());
                    var dateRange = shortcut.value;
                    setStartDate(dateRange[0]);
                    setEndDate(dateRange[1]);
                    onDateValueChange(dateRange[0], dateRange[1]);
                }
            }, onClean: function () {
                setShortCut(null);
            } }),
        showLabel && (React.createElement(FormLabel, { className: "label", style: {
                transform: "".concat((startDate && endDate) || focus
                    ? "scale(0.85) translateY(".concat(size === "sm" ? "-16px" : "-24px", ")")
                    : ""),
                top: "-3px",
                marginLeft: !focus && size === "sm" ? "8px" : "12px",
            }, sx: {
                display: "flex",
                alignItems: "center",
                height: size === "sm" ? 5 : 8,
            } }, name)),
        !isInvalid && helpText && (React.createElement(FormHelperText, { display: "flex", gap: 1, justifyContent: "flex-start", alignItems: "center" },
            React.createElement(Question, { w: 3, h: 3 }),
            " ",
            React.createElement(Typography, { color: "neutrals.600", variant: "small" }, helpText))),
        isInvalid && (React.createElement(FormErrorMessage, { display: "flex", gap: 1 },
            React.createElement(CloseFilled, { w: 3, h: 3 }),
            " ",
            React.createElement(Typography, { variant: "small", color: "error.700" },
                error,
                " "),
            " ")),
        React.createElement("style", null, DateRangePickerStyles.styles)));
};

export { DateRangePicker as default };
//# sourceMappingURL=DateRangePicker.js.map
